<script setup lang="ts">
import ATitle from '@/components/io/atomic/atoms/fragments/content/ATitle.vue'
import Button from '@/components/Button.vue'
import MPDImage from '@/components/MPDImage.vue'
import FrameTitleDirectory from '@/components/frames/FrameTitleDirectory.vue'
import SectionContactWithRows from "@/components/sections/SectionContactWithRows.vue"

useSeoMeta({
  title: 'About Altracare | Zanzibar Hygiene Partner',
  description: 'Discover Altracare’s mission, eco-friendly approach and Halal-certified hygiene expertise supporting Zanzibar hospitality and healthcare leaders.',
  ogTitle: 'About Altracare | Zanzibar Hygiene Partner',
  ogDescription: 'Discover Altracare’s mission, eco-friendly approach and Halal-certified hygiene expertise supporting Zanzibar hospitality and healthcare leaders.',
  ogType: 'website',
  twitterCard: 'summary_large_image'
})

const { scrollToId } = useScrollTo()

const { hero } = useHero()
onMounted(() => {
  hero({
    media: {
      type: 'image',
      media: {
        src: '/images/housekeeping.jpg',
        alt: 'About Altracare',
      }
    },
    content: {
      model: 'page',
      content: {
        hairline: '',
        title: 'About Altracare',
        description: 'Trusted partners in hygiene and hospitality care across Zanzibar and beyond.'
      }
    },
    overlays: {
      transition: { mode: 'out-in' },
      overlays: [{
        type: 'gradient',
        styles: {
          kind: 'linear',
          direction: 180,
          stops: [
            { color: 'rgba(0,0,0,0.00)', at: '45%' },
            { color: 'rgba(0,0,0,0.75)', at: '80%' },
            { color: 'rgba(0,0,0,1.00)', at: '100%' }
          ],
          blendMode: 'normal'
        },
        breakpoints: [
          {
            from: 1024, until: -1,
            styles: {
              stops: [
                { color: 'rgba(0,0,0,0.00)', at: '40%' },
                { color: 'rgba(0,0,0,0.85)', at: '78%' },
                { color: 'rgba(0,0,0,1.00)', at: '100%' }
              ]
            }
          }
        ]
      }, {
        type: 'gradient',
        styles: {
          kind: 'linear',
          direction: 'to right',
          stops: [
            { color: 'rgba(0,40,140,0.45)', at: '0%' },
            { color: 'rgba(0,40,140,0.20)', at: '35%' },
            { color: 'rgba(0,40,140,0.00)', at: '70%' }
          ],
          blendMode: 'soft-light'
        },
        breakpoints: [
          {
            from: 1024, until: -1,
            styles: {
              direction: 'to bottom right',
              stops: [
                { color: 'rgba(0,40,140,0.50)', at: '0%' },
                { color: 'rgba(0,40,140,0.00)', at: '60%' }
              ],
              blendMode: 'multiply'
            }
          }
        ]
      }]
    },
  })
})

const features = [
{
  icon: 'i-mdi:rotate-360',
  title: '360° Service Model',
  description: 'A complete cycle of assessment, solutions, training, support, and refinement.',
  color: '#22d3ee'
},
{
  icon: 'i-mdi:leaf',
  title: 'Eco-Friendly by Design',
  description: 'High-performance products that protect people, spaces, and the planet.',
  color: '#34d399'
},
{
  icon: 'i-mdi:handshake',
  title: 'Local Expertise & Trusted',
  description: 'Locally committed and trusted by clients, offering tailored solutions that work in real-world hospitality and service settings.',
  color: '#60a5fa'
},
{
  icon: 'i-mdi:food-apple-outline',
  title: 'Halal Certified',
  description: 'Products and processes certified Halal, ensuring cultural alignment, inclusivity, and confidence for all clients.',
  color: '#a78bfa'
}
]
</script>

<template>
  <div class="page-about">
    <!-- Intro Section -->
    <Frame class="section-intro gpy-2 t:gpy-3" w="max-w-90% mx-auto ds:grid ds:grid-cols-[1fr_2fr] g-1 gpy-2 relative" >
      <Frame class="sec-content" w="grid g-1 sticky top-120px max-w-92% t:max-w-85% mx-auto gpb-3">
        <ATitle class="a-title sphere-content-title grid g-1">
          <template #hairline>
            <div class="a-title-hairline glh-1! text-[var(--heading-hairline)] gmb-0.5">
              Safety and efficiency serviced together.
            </div>
          </template>
          <template #primary>
            <div class="a-title-primary fs-5! glh-2!">
              More Than a Supplier. <span class="text-[var(--c-sky-blue)]"><br/>A Partner in Excellence.</span>
            </div>
          </template>
        </ATitle>
        <Frame w="mx-auto ds:grid g-1 justify-start">
          <p>At Altracare, we don’t just provide cleaning products, we deliver peace of mind. Our approach combines global hygiene standards with local expertise to help businesses create environments that are spotless, safe, and sustainable. Whether it’s a luxury resort, a busy kitchen, or a healthcare facility, we empower our clients to meet the highest standards every day.</p>
        </Frame>
      </Frame>
      <Frame class="sec-content" w="grid g-1 max-w-92% t:max-w-85% mx-auto">

        <Frame w="max-w-100% mx-auto grid g-2">

          <Frame class="vision-block" w="grid g-1">
            <div class="vm-label fs-0 uppercase text-gray-400 fw-600">Our Vision</div>
            <p class="fs-1 t:fs-15 fw-500 glh-1.5">
              To be Zanzibar's most trusted partner in hygiene excellence, helping businesses deliver safe, spotless, and sustainable environments.
            </p>
          </Frame>
          
          <!-- Mission -->
          <Frame class="mission-block" w="grid g-1">
            <div class="vm-label fs-0 uppercase text-gray-400 fw-600">Our Mission</div>
            <p class="fs-1 t:fs-15 fw-500 glh-1.5">
              We partner with clients to raise hygiene standards through world-class products, tailored solutions, and a proven 360° service model that ensures confidence and compliance.
            </p>
          </Frame>

          <Frame v-if="true" class="story-images gmt-2" w="grid grid-cols-[1fr_1fr] ds:grid-cols-[1fr_1fr_1fr_1fr] g-1">
            <MPDImage class="story-image" image="/images/grids/kitchen-grid-1.jpg" alt="Kitchen hygiene" />
            <MPDImage class="story-image" image="/images/bathroom.jpg" alt="Laundry solutions" />
            <MPDImage class="story-image" image="/images/food.jpg" alt="Food safety" />
            <MPDImage class="story-image" image="/images/grids/scrubbing-1.jpg" alt="Professional cleaning" />
          </Frame>
                
        </Frame>
        
      </Frame>
    </Frame>

    <!-- Our Story Section -->
    <FrameTitleDirectory class="section-story gmb-3" dir="ltr">
      <template #title>
        <Frame class="sticky-container t:sticky t:top-32">
          <Frame class="sec-story-title bg-white t:bg-transparent gpy-1">

            <ATitle class="a-title sphere-content-title">
              <template #hairline>
                <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                  Our Journey
                </div>
              </template>
              <template #primary>
                <div class="a-title-primary fs-5! glh-2!">
                  From Local Expertise to Global Standards
                </div>
              </template>
            </ATitle>
          </Frame>
        </Frame>
      </template>
      
      <template #directory>
        <Frame class="story-content grid g-2">
          <Frame class="story-text" w="grid g-1.5">
            <p class="fs-05 glh-1.5">
              Founded to serve Zanzibar's thriving hospitality and service industries, Altracare brings world-class eco-friendly hygiene solutions to one of the world's most dynamic regions.
            </p>
            <p class="fs-05 glh-1.5">
              By combining global supplier partnerships with local knowledge, we've grown into a trusted partner for hotels, resorts, restaurants, healthcare facilities, and beyond. Our commitment is to deliver the same professional results found in international markets — adapted for the needs and realities of our region.
            </p>
          </Frame>
          
         
        </Frame>
      </template>
    </FrameTitleDirectory>

    

    <Frame class="flex-1 bg-gray-50" w="gpy-3">

      <div class="sec-title text-center gmb-3 grid g-0.25">
        <div class="title fs-4! glh-2! text-[var(--base-650)]">
          Why Choose Us
        </div>
        <div class="description fs-05 text-[var(--base-600)] glh-1.25">
          The Altracare Difference
        </div>
      </div>
      
      <Frame class="features-grid grid grid-cols-[1fr]" w="mx-auto max-w-85% gc-300px ds:gc-300px g-1">
        <Frame 
          v-for="(feature, index) in features" 
          :key="index"
          class="feature-card bg-white rounded-lg gp-2 hover:shadow-lg transition-shadow"
        >
          <div class="feature-icon gmb-1">
            <i 
              :class="feature.icon" 
              class="fs-3" 
              :style="{ color: feature.color }"
            />
          </div>
          <h3 class="fs-05 fw-600 gmb-0.5 text-[var(--base-700)]">{{ feature.title }}</h3>
          <p class="fs--05 text-[var(--base-600)] glh-1.25">{{ feature.description }}</p>
        </Frame>
      </Frame>

    </Frame>

    <!-- Contact Section -->
    <SectionContactWithRows class="gpy-4" id="contact" />
  </div>
</template>

<style lang="scss" scoped>
.page-about {
  // Intro section styling
  .section-intro {
    .intro-statement {
      .title {
        line-height: gr(2.25);
      }
    }
  }

  // Story section
  .section-story {
    .story-images {
      .story-image {
        aspect-ratio: 1 / 1;
        overflow: hidden;
        border-radius: 0.5rem;
        
        :deep(img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        &:hover {
          :deep(img) {
            transform: scale(1.05);
          }
        }
      }
    }
  }

  // Features section
  .section-features {
    .feature-card {
      border: 1px solid var(--base-200);
      
      &:hover {
        border-color: var(--base-300);
        transform: translateY(-2px);
        transition: all 0.3s ease;
      }
    }
  }

  // Vision & Mission
  .section-vision-mission {
    .vm-label {
      letter-spacing: 0.05em;
    }
    
    .vision-block,
    .mission-block {
      padding: gr(2);
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid var(--base-200);
      border-radius: 0.75rem;
    }
  }
  
  // Responsive adjustments
  @include mq($until: t) {
    .section-intro {
      .intro-content {
        gap: gr(1.5);
      }
    }
    
    .section-story {
      .sticky-container {
        position: relative !important;
      }
    }
  }
}
</style>
